import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectUnitToken } from '@homeplay/app';
import { apiGetLotteryResults } from '@homeplay/api';
import { useCallOnceWhen } from '../../hooks/useCallOnceWhen';
import LotteryResultCard from '../LotteryResultCard';
import WidgetError from '../WidgetError';
import RefreshButton from '../RefreshButton';
import BotMessage from '../BotMessage';
import CountrySelect from '../CountrySelect';
import classes from './LotteriesLastResults.module.scss';

const PAGE_SIZE = 15;

const LotteriesLastResults = ({ intentArgs, onWidgetDataLoaded }) => {
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);
  const unitToken = useSelector(selectUnitToken);
  const prefetchedData = intentArgs?.data;
  const [ignorePrefetchedData, setIgnorePrefetchedData] = useState(false);
  const {
    country,
    lottery_name: lotteryName,
    from,
    to,
  } = intentArgs ?? {};
  const [selectedCountry, setSelectedCountry] = useState(country ?? null);

  const [data, setData] = useState(prefetchedData ?? null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!unitToken) return;
    setIsFetching(true);
    setError(null);
    try {
      const result = await apiGetLotteryResults({
        unitToken,
        country: selectedCountry ?? undefined,
        lotteryName,
      });
      setData(result);
    } catch (err) {
      setError(err);
    } finally {
      setIsFetching(false);
    }
  }, [unitToken, selectedCountry, lotteryName]);

  useEffect(() => {
    if (prefetchedData && !ignorePrefetchedData) return;
    fetchData();
  }, [fetchData, prefetchedData, ignorePrefetchedData]);

  const isDataLoaded = !!data || !!error;
  useCallOnceWhen(isDataLoaded, onWidgetDataLoaded);

  const lotteries = data?.lotteries_results ?? [];
  const totalLotteries = data?.count ?? lotteries.length;
  const availableCountries = data?.available_countries ?? [];
  const hasMore = totalLotteries > displayCount;

  const displayedLotteries = useMemo(
    () => lotteries.slice(0, displayCount),
    [displayCount, lotteries]
  );

  const handleCountryChange = (value) => {
    setSelectedCountry(value || null);
    setDisplayCount(PAGE_SIZE);
    setIgnorePrefetchedData(true);
  };

  const handleRefresh = () => {
    if (prefetchedData && !ignorePrefetchedData) {
      setIgnorePrefetchedData(true);
    } else {
      fetchData();
    }
  };

  if (!prefetchedData && error) {
    return <WidgetError onRetry={fetchData} error={error} />;
  }

  return (
    <div className={classes.Container}>
      <CountrySelect
        countries={availableCountries}
        value={selectedCountry}
        onChange={handleCountryChange}
        className={classes.CountryFilter}
      />

      <div className={classes.HeaderActions}>
        <RefreshButton onClick={handleRefresh} isFetching={isFetching} ariaLabel="Refresh results" />
        <span className={classes.HeaderSubtitle}>
          Latest lottery results ({lotteries.length}{' '}
          {lotteries.length === 1 ? 'lottery' : 'lotteries'})
        </span>
      </div>

      <div className={classes.Grid}>
        {lotteries.length === 0 ? (
          <BotMessage content="No lottery results found" withAvatar={false} />
        ) : (
          displayedLotteries.map((lottery) => (
            <LotteryResultCard
              key={lottery.lottery_id}
              lottery={lottery}
              from={from}
              to={to}
            />
          ))
        )}
      </div>

      {hasMore && (
        <div className={classes.LoadMoreWrapper}>
          <button
            type="button"
            onClick={() => setDisplayCount((prev) => prev + PAGE_SIZE)}
            className={classes.LoadMoreButton}
          >
            Show More
          </button>
        </div>
      )}
    </div>
  );
};

export default LotteriesLastResults;
